import { isOnFreePlan } from '@/guards/free-plan-access';
import { PLAN_IDS } from '@/lib/payload';
import {
  PLAN_FEE_GRID,
  type OrganizationBillingContext,
  type OrganizationBillingOffers,
  type CurrentSubscription,
  type PlanCollabFee,
  type PlanResponseData,
  type User,
} from '@fluenceur/types';

export const PLAN_CHOICE_IDS = ['free', 'essential', 'growth'] as const;
export type PlanChoiceId = (typeof PLAN_CHOICE_IDS)[number];

export type PlanChoice = {
  id: PlanChoiceId;
  price: PlanResponseData['price'] | null;
  successFeeBasisPoints: number;
  /** Charged to the brand on top of what a paid collab promises the creator. */
  collabFee: PlanCollabFee;
  giftingFeeBasisPoints: number;
  /** The public catalogue price, shown while the organization has no offer to commit to. */
  indicative?: boolean;
};

/** Which payment line the footer shows: none for free, else the rail the brand pays on. */
export type BillingLine = 'free' | 'shopify' | 'stripe';

/** An approved Free usage carrier is not a paid plan replacement. */
export function isActivePaidSubscription(
  subscription: Pick<CurrentSubscription, 'status' | 'planId'> | null | undefined
): boolean {
  return (
    subscription?.status === 'active' &&
    [PLAN_IDS.ESSENTIAL, PLAN_IDS.GROWTH].some((planId) => planId === subscription.planId)
  );
}

/** A future paid quote follows the canonical payer, independently of the old usage currency. */
export function readShopifyPlanCatalogue(
  context: OrganizationBillingContext | undefined,
  selectedShopCurrency?: string
) {
  const isCanonicalFree =
    context?.channel === 'shopify' &&
    !!context.shopifyFreeApproval &&
    context.shopifyFreeApproval.requirement !== 'not_required';
  const currency = context?.shopifyFreeApproval?.catalogCurrency;
  return {
    isCanonicalFree,
    currency:
      isCanonicalFree && currency !== undefined ? (currency ?? undefined) : selectedShopCurrency,
    unavailable: isCanonicalFree && currency === null,
  };
}

/**
 * The three plans of the choice, free first. Null when the catalogue lacks a
 * paid plan: a choice missing one of its options is not a choice to show.
 */
export function buildPlanChoices(
  plans: PlanResponseData[] | undefined
): Array<PlanChoice & { price: PlanResponseData['price'] }> | null {
  const essential = plans?.find((plan) => plan.id === PLAN_IDS.ESSENTIAL);
  const growth = plans?.find((plan) => plan.id === PLAN_IDS.GROWTH);
  if (!essential || !growth) return null;

  // The free offer is not a plan of the catalogue, so its fees come from the
  // shared grid the server charges from; the paid plans' come from the API.
  const free = PLAN_FEE_GRID.free;
  const paidChoice = (id: 'essential' | 'growth', plan: PlanResponseData) => ({
    id,
    price: plan.price,
    successFeeBasisPoints: plan.successFee.basisPoints,
    collabFee: plan.collabFee,
    giftingFeeBasisPoints: plan.giftingFeeBasisPoints,
  });

  return [
    {
      id: 'free',
      price: { amount: 0, currencyCode: essential.price.currencyCode },
      successFeeBasisPoints: free.successFeeBasisPoints,
      collabFee: free.collabFee,
      giftingFeeBasisPoints: free.giftingFeeBasisPoints,
    },
    paidChoice('essential', essential),
    paidChoice('growth', growth),
  ];
}

/**
 * A paid plan is quoted at the server's Stripe offer when the organization has
 * one. Until it can be read (no tax residence yet, a bypassed or manually
 * billed organization), the public catalogue price stands in, but only in the
 * billing currency: a price is never quoted in a currency the brand is not
 * billed in. Checkout itself still waits for the offer.
 */
export function buildBillingPlanChoices(
  plans: PlanResponseData[] | undefined,
  context: OrganizationBillingContext | undefined,
  offers: OrganizationBillingOffers | undefined
): PlanChoice[] | null {
  const choices = buildPlanChoices(plans);
  if (!choices) return choices;
  if (context?.channel === 'shopify')
    return readShopifyPlanCatalogue(context).unavailable
      ? choices.map((choice) => ({ ...choice, price: null }))
      : choices;
  const scopedOffers =
    context?.channel === 'stripe' &&
    offers?.organizationId === context.organizationId &&
    offers.brandId === context.brandId &&
    offers.currency === context.billingCurrency
      ? offers
      : undefined;
  return choices.map((choice) => {
    if (choice.id === 'free')
      return {
        ...choice,
        price: context?.billingCurrency
          ? { amount: 0, currencyCode: context.billingCurrency }
          : null,
      };
    const offer = scopedOffers?.offers.find(
      (candidate) =>
        candidate.planId === PLAN_IDS[choice.id.toUpperCase() as 'ESSENTIAL' | 'GROWTH']
    );
    const cataloguePrice =
      context?.billingCurrency && choice.price.currencyCode === context.billingCurrency
        ? choice.price
        : null;
    if (offer)
      return { ...choice, price: { amount: offer.unitAmount / 100, currencyCode: offer.currency } };
    return cataloguePrice
      ? { ...choice, price: cataloguePrice, indicative: true }
      : { ...choice, price: null };
  });
}

/**
 * The Free plan's monthly price, in the currency the plans on screen are quoted
 * in. Copy that names this amount reads it from here rather than writing a
 * currency of its own: a Shopify catalogue can be in dollars.
 */
export function freePlanPrice(choices: PlanChoice[] | null): PlanChoice['price'] {
  return choices?.find((choice) => choice.id === 'free')?.price ?? null;
}

/** Apply a scoped server offer to existing plan metadata without currency conversion. */
export function billingPlanWithOffer(
  plan: PlanResponseData | undefined,
  context: OrganizationBillingContext | undefined,
  offers: OrganizationBillingOffers | undefined
): PlanResponseData | undefined {
  if (!plan) return plan;
  if (context?.channel === 'shopify')
    return readShopifyPlanCatalogue(context).unavailable ? undefined : plan;
  const scopedOffers =
    context?.channel === 'stripe' &&
    offers?.organizationId === context.organizationId &&
    offers.brandId === context.brandId &&
    offers.currency === context.billingCurrency
      ? offers
      : undefined;
  const offer = scopedOffers?.offers.find((candidate) => candidate.planId === plan.id);
  return offer
    ? { ...plan, price: { amount: offer.unitAmount / 100, currencyCode: offer.currency } }
    : undefined;
}

/**
 * The choice is offered once, to a free organization arriving on its dashboard
 * from its onboarding, until it decides. Never to one that pays: the free plan
 * date stays on an organization that later subscribes.
 */
export function shouldOfferPlanChoice(
  user: User | null | undefined,
  { decided, hasActiveSubscription }: { decided: boolean; hasActiveSubscription: boolean }
): boolean {
  return (
    isOnFreePlan(user) &&
    !!user?.currentBrand?.onboarding?.completedAt &&
    !decided &&
    !hasActiveSubscription
  );
}

/** A Shopify brand pays on its Shopify invoice; every other brand through Stripe. */
export const billingLineFor = (choice: PlanChoiceId, isShopifyBrand: boolean): BillingLine =>
  choice === 'free' ? 'free' : isShopifyBrand ? 'shopify' : 'stripe';
